import { Component, Input, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { PaginatedListOfGetResellerPaymentsForConfirmWithPaginationDto } from 'src/app/core/api/models';
import { ExcelService, ResellersService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { TextPopoverDirective } from 'src/app/core/directives/text-popover.directive';
import { AppDomService } from 'src/app/core/services/dom.service';
import { BaseIndexModule } from 'src/app/pages/shared/base/modules/base-index.module';
import { PaymentsService } from '../../../../core/api/services/payments.service';
import { confirmDialogConfig } from '../../../../core/services/dialog.service';
import { DynamicTabService } from '../../../../core/services/dynamic-tab.service';
import { UtilityService } from '../../../../core/services/utility.service';
import { BasePaymentsComponent } from '../../../financial/payments/base/base-payments.component';
import { BasePaymentDetailsComponent } from '../../payments/base/base-payment-details/base-payment-details.component';
import { ShowPaymentImageModalComponent } from '../../payments/base/show-payment-image-modal/show-payment-image-modal.component';
import { PaginatedListOfGetPaymentsForConfirmWithPaginationDto } from './../../../../core/api/models/paginated-list-of-get-payments-for-confirm-with-pagination-dto';
import { TypePaymentStatus } from './../../../../core/api/models/type-payment-status';
import { TypePayments } from './../../../../core/api/models/type-payments';

@Component({
  selector: 'app-base-payments-for-confirm',
  imports: [BaseIndexModule, TextPopoverDirective, BasePaymentDetailsComponent],
  templateUrl: './base-payments-for-confirm.component.html',
})
export class BasePaymentsForConfirmComponent
  extends BasePaymentsComponent
  implements OnInit
{
  //#region Properties
  protected currentService = inject(PaymentsService);
  auditApiMethodName: string =
    this.currentService.paymentsGetPaymentsAudit.name;
  modelWithPagination$: Observable<
    | PaginatedListOfGetPaymentsForConfirmWithPaginationDto
    | PaginatedListOfGetResellerPaymentsForConfirmWithPaginationDto
  >;
  items$: Observable<Array<any>>;

  @Input() getTableDataApiMethodName: string;

  @Input() pageTitle: string;

  //#region config table

  pageVocabs = 'pages.paymentsForConfirm.';
  get TypePaymentStatus() {
    return TypePaymentStatus;
  }
  get typePayment() {
    return TypePayments;
  }

  //#endregion

  //#endregion

  constructor(
    public resellerService: ResellersService,
    public excelFileService: ExcelService,
    protected utilityService: UtilityService,
    public dynamicTabService: DynamicTabService
  ) {
    super(resellerService, utilityService, dynamicTabService, excelFileService);
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.pageConfig.title = this.pageTitle;
      this.pageConfig.isAddBtnWithLoading = false;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  configTableColumns() {
    this.tableVocabs = 'pages.payments.fields.';
    this.tableColumns = [
      {
        title: this.localizer.id,
        name: 'id',
        hasSearch: true,
        type: 'number',
        wrap: true,
        noNumberSeparator: true,
      },
      {
        title: this.localizer.amount,
        name: 'amount',
        hasSearch: true,
        needCurrency: true,
        type: 'number',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}smsCount`,
        name: 'smsCount',
        type: 'number',
        hasSearch: false,
        noSorting: true,
        wrap: true,
      },
      {
        title: this.localizer.user,
        name: 'userName',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.organization,
        name: 'organizationName',
        hasSearch: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}typePayment`,
        name: 'typePayment',
        hasSearch: true,
        type: 'enum',
        enumName: 'TypePayments',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}isOnlineGeneral`,
        name: 'isOnlineGeneral',
        type: 'boolean',
        hasSearch: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}accountInformationBank`,
        name: 'accountInformationBank',
        hasSearch: true,
        type: 'enum',
        enumName: 'TypeBanks',
        wrap: true,
      },
      {
        title: `${this.tableVocabs}accountInformationAccountNo`,
        name: 'accountInformationAccountNo',
        hasSearch: true,
        wrap: true,
        dir: 'ltr',
      },
      {
        title: 'common.owner',
        name: 'accountInformationOwner',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.domain,
        name: 'domainName',
        hasSearch: true,
        wrap: true,
      },
      {
        title: this.localizer.status,
        name: 'typeStatus',
        hasSearch: true,
        type: 'enum',
        enumName: 'TypePaymentStatus',
        wrap: true,
      },

      {
        title: `${this.tableVocabs}smsCountPerPriceDescription`,
        name: 'smsCountPerPriceDescription',
        hasSearch: false,
        noSorting: true,
        wrap: true,
      },
      {
        title: `${this.tableVocabs}paymentDate`,
        name: 'paymentDate',
        hasSearch: true,
        type: 'dateTime',
        wrap: true,
      },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
        wrap: true,
      },
    ];
  }

  //#region api methods

  //Get All
  getPaginationApi(): Observable<any> {
    try {
      return this.currentService[this.getTableDataApiMethodName]({
        body: this.getBaseModelWithPaginationParams(),
      }).pipe(tap((items: any) => this.calculateTotalSum(items?.data?.items)));
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  //only for super admin
  confirmPayment(paymentId: number) {
    this.unSubs();

    this.pageConfig.loading = true;

    this.currentService
      .paymentsConfirmPayment({ body: { selectedPaymentId: paymentId } })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //only for super admin
  rejectPayment(paymentId: number) {
    this.unSubs();

    this.pageConfig.loading = true;

    this.currentService
      .paymentsRejectPayment({ body: { selectedPaymentId: paymentId } })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //#endregion

  //#region confirm and reject payment modals
  //confirm
  onConfirm(paymentId: number) {
    try {
      const config: confirmDialogConfig = {
        title: 'messages.confirmPayment',
        content: paymentId.toString(),
      };

      const dialogResult = this.getConfirmDialog(config);

      dialogResult
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result) this.confirmPayment(paymentId);
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  //reject
  onReject(paymentId: number) {
    try {
      const config: confirmDialogConfig = {
        title: 'messages.rejectPayment',
        content: paymentId.toString(),
        confirmBtnColor: 'btn-danger',
      };

      const dialogResult = this.getConfirmDialog(config);

      dialogResult
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result) this.rejectPayment(paymentId);
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.financial);
    }
  }

  onShowImage(id) {
    this.currentService
      .paymentsDownloadPaymentImage({ PaymentId: id })
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        const modalResult = this.dialogService.openModal({
          component: ShowPaymentImageModalComponent,
          data: res?.data,
        });

        modalResult
          .afterClosed()
          .pipe(
            takeUntil(this.onDestroy$),
            tap(() => {
              AppDomService.removeModalScrollClassFromHtml();
            })
          )
          .subscribe();
      });
  }

  //#endregion
}
